import { appColors } from '../Config/Theme';
import type { RosterScopeCountsDto } from '../Types/Incident';

// ── Dashboard counts ──

export interface DashboardCounts {
  total: number;
  accounted: number;
  notAccounted: number;
  notChecked: number;
  accountabilityPercent: number;
  unsyncedChanges: number;
  fullRosterTotal: number;
  assignedOfficeNoBadge: number;
  needsAttentionCount: number;
  visitorCount: number;
}

export const getDashboardCounts = (summary: {
  total: number;
  accounted: number;
  notAccounted: number;
  notChecked: number;
  unsyncedChanges?: number;
  assignedOfficeNotBadgedIn?: number;
  visitorCount?: number;
  scopeCounts?: RosterScopeCountsDto;
}): DashboardCounts => {
  // Prefer API-provided scope counts when available
  if (summary.scopeCounts) {
    const ac = summary.scopeCounts.accountabilityCounts;
    const fr = summary.scopeCounts.fullRosterCounts;
    return {
      total: ac.total,
      accounted: ac.accounted,
      notAccounted: ac.notAccounted,
      notChecked: ac.notChecked,
      accountabilityPercent: ac.percentComplete,
      unsyncedChanges: summary.unsyncedChanges ?? 0,
      fullRosterTotal: fr.total,
      assignedOfficeNoBadge: summary.scopeCounts.assignedOfficeNoBadgeTodayNotChecked ?? 0,
      needsAttentionCount: summary.scopeCounts.needsAttentionCount ?? fr.notAccounted,
      visitorCount: summary.scopeCounts.visitorCount ?? summary.visitorCount ?? 0,
    };
  }

  // Derive from flat counts
  const assignedNoBadge = summary.assignedOfficeNotBadgedIn ?? 0;
  const accountabilityTotal = Math.max(0, summary.total - assignedNoBadge);
  const accountabilityNotChecked = Math.max(0, accountabilityTotal - summary.accounted - summary.notAccounted);

  return {
    total: accountabilityTotal,
    accounted: summary.accounted,
    notAccounted: summary.notAccounted,
    notChecked: accountabilityNotChecked,
    accountabilityPercent: accountabilityTotal === 0 ? 0 : Math.round((summary.accounted / accountabilityTotal) * 100),
    unsyncedChanges: summary.unsyncedChanges ?? 0,
    fullRosterTotal: summary.total,
    assignedOfficeNoBadge: assignedNoBadge,
    needsAttentionCount: summary.notAccounted,
    visitorCount: summary.visitorCount ?? 0,
  };
};

// ── Dashboard state ──

export type DashboardState =
  | 'closed'
  | 'emptyRoster'
  | 'needsAttention'
  | 'complete'
  | 'notStarted'
  | 'inProgress';

export const getIncidentDashboardState = (
  counts: DashboardCounts,
  incidentStatus?: string,
): DashboardState => {
  if (incidentStatus && ['closed', 'completed', 'complete', 'inactive'].includes(incidentStatus.toLowerCase().trim())) {
    return 'closed';
  }
  if (counts.total === 0) return 'emptyRoster';
  if (counts.notAccounted > 0) return 'needsAttention';
  if (counts.notChecked === 0 && counts.notAccounted === 0 && counts.total > 0) return 'complete';
  if (counts.accounted === 0 && counts.notAccounted === 0 && counts.notChecked === counts.total) return 'notStarted';
  return 'inProgress';
};

// ── Primary action ──

export interface DashboardAction {
  label: string;
  route: string;
  variant: 'contained' | 'outlined';
  color?: 'primary' | 'error' | 'inherit';
}

export const getPrimaryDashboardAction = (
  state: DashboardState,
  incidentId: string,
): DashboardAction => {
  switch (state) {
    case 'notStarted':
      return { label: 'Start Headcount', route: `/incidents/${incidentId}/headcount`, variant: 'contained', color: 'primary' };
    case 'inProgress':
      return { label: 'Continue Headcount', route: `/incidents/${incidentId}/headcount`, variant: 'contained', color: 'primary' };
    case 'needsAttention':
      return { label: 'Review Not Accounted', route: `/incidents/${incidentId}/headcount?status=notAccounted`, variant: 'contained', color: 'error' };
    case 'complete':
      return { label: 'Review & Close Incident', route: '', variant: 'contained', color: 'primary' };
    case 'closed':
      return { label: 'View Report', route: `/incidents/${incidentId}/headcount`, variant: 'outlined', color: 'primary' };
    case 'emptyRoster':
      return { label: 'Refresh', route: '', variant: 'outlined', color: 'primary' };
  }
};

// ── Quick actions ──

export interface QuickAction {
  label: string;
  route: string;
  urgent?: boolean;
}

export const getQuickActions = (
  state: DashboardState,
  counts: DashboardCounts,
  incidentId: string,
): QuickAction[] => {
  switch (state) {
    case 'notStarted':
      return [
        { label: 'Start Headcount', route: `/incidents/${incidentId}/headcount` },
        { label: 'Choose Roster View', route: `/incidents/${incidentId}/views` },
        { label: 'Open Full Roster', route: `/incidents/${incidentId}/headcount` },
      ];
    case 'inProgress':
      return [
        { label: 'Continue Headcount', route: `/incidents/${incidentId}/headcount` },
        ...(counts.notChecked > 0 ? [{ label: 'View Not Checked', route: `/incidents/${incidentId}/headcount?status=notChecked` }] : []),
        { label: 'Open Full Roster', route: `/incidents/${incidentId}/headcount` },
      ];
    case 'needsAttention':
      return [
        { label: 'Review Not Accounted', route: `/incidents/${incidentId}/headcount?status=notAccounted`, urgent: true },
        ...(counts.notChecked > 0 ? [{ label: 'View Not Checked', route: `/incidents/${incidentId}/headcount?status=notChecked` }] : []),
        { label: 'Continue Headcount', route: `/incidents/${incidentId}/headcount` },
      ];
    case 'complete':
      return [
        { label: 'Review Report', route: `/incidents/${incidentId}/headcount` },
      ];
    case 'closed':
      return [
        { label: 'View Report', route: `/incidents/${incidentId}/headcount` },
        { label: 'Open Read-Only Roster', route: `/incidents/${incidentId}/headcount` },
      ];
    case 'emptyRoster':
      return [];
  }
};

// ── Next action panel content ──

export interface NextActionContent {
  title: string;
  body: string;
  secondaryBody?: string;
  guidance?: string;
  icon: 'start' | 'continue' | 'attention' | 'complete' | 'empty' | 'closed';
  borderColor: string;
  bgColor: string;
}

export const getNextActionContent = (
  state: DashboardState,
  counts: DashboardCounts,
): NextActionContent => {
  switch (state) {
    case 'notStarted':
      return {
        title: 'Headcount not started',
        body: `${counts.notChecked} ${counts.notChecked === 1 ? 'person' : 'people'} still need to be checked.`,
        icon: 'start',
        borderColor: appColors.primary.main,
        bgColor: `${appColors.primary.main}08`,
      };
    case 'inProgress':
      return {
        title: 'Headcount in progress',
        body: `${counts.accounted} of ${counts.total} accounted \u00B7 ${counts.notChecked} not checked`,
        icon: 'continue',
        borderColor: appColors.primary.main,
        bgColor: `${appColors.primary.main}08`,
      };
    case 'needsAttention':
      return {
        title: 'Not accounted',
        body: `${counts.notAccounted} ${counts.notAccounted === 1 ? 'person is' : 'people are'} marked Not Accounted.`,
        secondaryBody: counts.notChecked > 0
          ? `${counts.notChecked} ${counts.notChecked === 1 ? 'person is' : 'people are'} still Not Checked.`
          : undefined,
        guidance: 'Review people marked Not Accounted before closing the incident or recording final progress.',
        icon: 'attention',
        borderColor: appColors.status.error,
        bgColor: `${appColors.status.error}08`,
      };
    case 'complete':
      return {
        title: 'Headcount complete',
        body: 'All people in the accountability roster have been resolved.',
        icon: 'complete',
        borderColor: appColors.status.success,
        bgColor: appColors.status.successBg,
      };
    case 'emptyRoster':
      return {
        title: 'Roster not available',
        body: 'No people are currently available in the incident roster.',
        icon: 'empty',
        borderColor: appColors.border.default,
        bgColor: appColors.surface.card,
      };
    case 'closed':
      return {
        title: 'Incident closed',
        body: 'This incident is read-only.',
        icon: 'closed',
        borderColor: appColors.border.default,
        bgColor: appColors.surface.card,
      };
  }
};

// ── Close incident warning ──

export const getCloseIncidentWarning = (counts: DashboardCounts): string => {
  if (counts.notAccounted > 0) {
    return `${counts.notAccounted} ${counts.notAccounted === 1 ? 'person is' : 'people are'} not accounted for. Are you sure you want to close this incident?`;
  }
  if (counts.notChecked > 0) {
    return `${counts.notChecked} ${counts.notChecked === 1 ? 'person is' : 'people are'} still not checked. Are you sure you want to close this incident?`;
  }
  return 'Close this incident? The roster will become read-only.';
};

// ── Progress bar styling ──

export const getProgressBarColor = (
  state: DashboardState,
  pct: number,
): string => {
  if (state === 'complete') return appColors.status.success;
  if (pct >= 90) return appColors.status.success;
  if (state === 'needsAttention') return appColors.status.errorDark;
  if (state === 'notStarted') return appColors.text.caption;
  return appColors.status.errorDark;
};

export const getProgressLabel = (
  state: DashboardState,
  counts: DashboardCounts,
): string => {
  switch (state) {
    case 'notStarted':
      return `Headcount not started \u2014 0 of ${counts.total} accountability total`;
    case 'inProgress': {
      const parts = [`${counts.accounted} of ${counts.total} accountability total`];
      if (counts.notChecked > 0) parts.push(`${counts.notChecked} pending review`);
      return parts.join(' \u00B7 ');
    }
    case 'needsAttention': {
      const parts = [`${counts.accounted} of ${counts.total} accountability total`, `${counts.needsAttentionCount} not accounted`];
      if (counts.notChecked > 0) parts.push(`${counts.notChecked} pending review`);
      return parts.join(' \u00B7 ');
    }
    case 'complete':
      return `${counts.accounted} of ${counts.total} accountability total`;
    case 'closed':
      return `${counts.accounted} of ${counts.total} accountability total`;
    case 'emptyRoster':
      return 'No roster data';
  }
};

// ── Metric card style ──

export const getMetricCardColor = (
  metric: 'total' | 'accounted' | 'notAccounted' | 'notChecked',
  value: number,
): string => {
  switch (metric) {
    case 'total':
      return appColors.primary.main;
    case 'accounted':
      return appColors.status.success;
    case 'notAccounted':
      return value > 0 ? appColors.status.error : appColors.text.caption;
    case 'notChecked':
      return appColors.text.body;
  }
};
